import React from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage} from "react-intl";

import {Formik, Form as FormikForm} from "formik";

import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";

import FormBloc from "components/form/FormBloc";
import FormikInput from "components/input/new/FormikInput";
import ActionsContainer from "components/containers/ActionsContainer";
import CancelButtonStyled from "components/button/CancelButtonStyled";
import SaveButton from "components/button/SaveButton";

import {warningColor, greyishBlue500} from "assets/jss/main";

import update from "api/invoiceSequenceConfiguration/update";

const EXAMPLE = {year: '2026', retailer: 'BHV', sequence: '42', padded: '000042'};
const RETAILER_TOKEN = '{retailer}';

// Renders an example invoice number from the given pattern, replacing tokens
// with sample values (soft preview — mirrors the back `formatReference`).
function renderExample(pattern, perRetailer, code) {
    if (!pattern) {
        return '';
    }

    return pattern
        .replace(/\{year\}/g, EXAMPLE.year)
        .replace(/##%06d##/g, EXAMPLE.padded)
        .replace(/\{sequence\}/g, EXAMPLE.sequence)
        .replace(/\{code\}/g, code || '')
        .replace(/\{retailer\}/g, perRetailer ? EXAMPLE.retailer : '');
}

ConfigForm.propTypes = {
    configuration: PropTypes.object.isRequired,
    onCancel: PropTypes.func.isRequired,
    onSaved: PropTypes.func.isRequired,
    onError: PropTypes.func.isRequired,
};

function ConfigForm({configuration, onCancel, onSaved, onError}) {
    const initialValues = {
        pattern: configuration.pattern || '',
        perRetailer: configuration.perRetailer || false,
        perYear: configuration.perYear || false,
        isDefault: configuration.isDefault || false,
    };

    return (
        <Formik
            initialValues={initialValues}
            enableReinitialize
            onSubmit={(values, {setSubmitting}) => {
                update(configuration, {
                    pattern: values.pattern,
                    perRetailer: values.perRetailer,
                    perYear: values.perYear,
                    isDefault: values.isDefault,
                })
                    .then(() => onSaved())
                    .catch(() => onError())
                    .finally(() => setSubmitting(false));
            }}
        >
            {({values, isSubmitting}) => {
                const example = renderExample(values.pattern, values.perRetailer, configuration.configurationCode);
                const showRetailerWarning = !values.perRetailer && (values.pattern || '').includes(RETAILER_TOKEN);

                return (
                    <FormikForm>
                        <Grid container rowSpacing={4}>
                            <FormBloc
                                titleId={'invoiceNumbering.form.title'}
                                subtitleId={'invoiceNumbering.form.subtitle'}
                            >
                                <Grid container rowSpacing={3}>
                                    <Grid item xs={12}>
                                        <FormikInput
                                            type={'string'}
                                            name={'pattern'}
                                            label={'invoiceNumbering.form.pattern.label'}
                                            placeholder={'invoiceNumbering.form.pattern.placeholder'}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Box sx={{fontSize: '13px', color: greyishBlue500, lineHeight: 1.6}}>
                                            <strong><FormattedMessage id={'invoiceNumbering.form.tokens.title'} /></strong>
                                            <Box component={'ul'} sx={{margin: '4px 0 0', paddingLeft: '20px'}}>
                                                <li><code>{'{year}'}</code> — <FormattedMessage id={'invoiceNumbering.form.tokens.year'} /></li>
                                                <li><code>{'{retailer}'}</code> — <FormattedMessage id={'invoiceNumbering.form.tokens.retailer'} /></li>
                                                <li><code>{'{sequence}'}</code> — <FormattedMessage id={'invoiceNumbering.form.tokens.sequence'} /></li>
                                                <li><code>##%06d##</code> — <FormattedMessage id={'invoiceNumbering.form.tokens.padded'} /></li>
                                                <li><code>{'{code}'}</code> — <FormattedMessage id={'invoiceNumbering.form.tokens.code'} /></li>
                                            </Box>
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Box sx={{fontSize: '14px'}}>
                                            <FormattedMessage id={'invoiceNumbering.form.preview.label'} />{' '}
                                            <span style={{fontFamily: 'monospace', fontWeight: 600}}>
                                                {example || '—'}
                                            </span>
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <FormikInput
                                            type={'switch'}
                                            name={'perRetailer'}
                                            label={'invoiceNumbering.form.perRetailer.label'}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormikInput
                                            type={'switch'}
                                            name={'perYear'}
                                            label={'invoiceNumbering.form.perYear.label'}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormikInput
                                            type={'switch'}
                                            name={'isDefault'}
                                            label={'invoiceNumbering.form.isDefault.label'}
                                        />
                                    </Grid>

                                    {showRetailerWarning && (
                                        <Grid item xs={12}>
                                            <Box sx={{color: warningColor, fontSize: '13px'}}>
                                                <FormattedMessage id={'invoiceNumbering.form.warning.retailerToken'} />
                                            </Box>
                                        </Grid>
                                    )}
                                </Grid>
                            </FormBloc>

                            <Grid item xs={12}>
                                <ActionsContainer>
                                    <CancelButtonStyled disabled={isSubmitting} onClick={onCancel}>
                                        <FormattedMessage id={'list.button.cancel'} />
                                    </CancelButtonStyled>
                                    <SaveButton type={'submit'} loading={isSubmitting} disabled={isSubmitting}>
                                        <FormattedMessage id={'invoiceNumbering.form.button.save'} />
                                    </SaveButton>
                                </ActionsContainer>
                            </Grid>
                        </Grid>
                    </FormikForm>
                );
            }}
        </Formik>
    );
}

export default ConfigForm;
